import React, { useEffect, useState } from "react";
import {
  ArrowUp,
  ArrowDown,
  Clock,
  Users,
  AlertCircle,
  CheckCircle2,
  Briefcase,
  Calendar,
  Activity,
  PieChart,
  BarChart,
  Layers,
  Settings,
  Bell,
  DollarSign,
  CalendarCheck,
} from "lucide-react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import BillingItemsService from "../../../services/BillingItemsService";
import { FaRupeeSign } from "react-icons/fa";

const InternDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");

  // Hardcoded data for dashboard
  const stats = [
    {
      title: "Active Projects",
      value: 14,
      icon: <Briefcase size={20} />,
      change: "+3",
      isPositive: true,
      color: "text-blue-600 bg-blue-100",
    },
    {
      title: "Total Clients",
      value: 32,
      icon: <Users size={20} />,
      change: "+5",
      isPositive: true,
      color: "text-green-600 bg-green-100",
    },
    {
      title: "Open Tickets",
      value: 47,
      icon: <AlertCircle size={20} />,
      change: "-8",
      isPositive: true,
      color: "text-purple-600 bg-purple-100",
    },
    {
      title: "Attendance Rate",
      value: "96%",
      icon: <CalendarCheck size={20} />,
      change: "+2%",
      isPositive: true,
      color: "text-indigo-600 bg-indigo-100",
    },
  ];

  // Pie chart data
  const projectStatusData = [
    { name: "Active", value: 14, color: "#3b82f6" },
    { name: "Planning", value: 4, color: "#8b5cf6" },
    { name: "Paused", value: 2, color: "#f59e0b" },
    { name: "Completed", value: 8, color: "#10b981" },
    { name: "Cancelled", value: 1, color: "#ef4444" },
  ];

  // Bar chart data
  const ticketsByDepartmentData = [
    { name: "Development", open: 18, inProgress: 12, resolved: 24 },
    { name: "Design", open: 8, inProgress: 6, resolved: 15 },
    { name: "QA", open: 14, inProgress: 9, resolved: 11 },
    { name: "DevOps", open: 7, inProgress: 4, resolved: 9 },
  ];

  const notifications = [
    {
      id: 1,
      title: "New client registration",
      message: "TechVision Inc. has registered as a new client",
      time: "10 minutes ago",
      isRead: false,
    },
    {
      id: 2,
      title: "Urgent ticket created",
      message: "Payment gateway issue reported by NextGen Retail",
      time: "45 minutes ago",
      isRead: false,
    },
    {
      id: 3,
      title: "Project milestone reached",
      message: "Phase 2 of CRM System completed successfully",
      time: "2 hours ago",
      isRead: true,
    },
    {
      id: 4,
      title: "Team meeting reminder",
      message: "Weekly team meeting starts in 30 minutes",
      time: "3 hours ago",
      isRead: true,
    },
  ];

  const recentTickets = [
    {
      id: "TCK-1025",
      title: "Payment Gateway Integration",
      priority: "HIGH",
      status: "OPEN",
      client: "NextGen Retail",
      assignedTo: "Michael Chen",
      date: "2 hours ago",
    },
    {
      id: "TCK-1024",
      title: "User Authentication Bug",
      priority: "URGENT",
      status: "IN_PROGRESS",
      client: "TechCorp Inc.",
      assignedTo: "Sarah Johnson",
      date: "4 hours ago",
    },
    {
      id: "TCK-1023",
      title: "Mobile App Crash on Startup",
      priority: "HIGH",
      status: "OPEN",
      client: "Global Systems",
      assignedTo: "David Smith",
      date: "1 day ago",
    },
    {
      id: "TCK-1022",
      title: "Dashboard Data Visualization",
      priority: "MEDIUM",
      status: "PENDING",
      client: "Acme LLC",
      assignedTo: "Emily Wilson",
      date: "1 day ago",
    },
    {
      id: "TCK-1021",
      title: "API Rate Limiting Issue",
      priority: "HIGH",
      status: "IN_PROGRESS",
      client: "TechVision Inc.",
      assignedTo: "James Rodriguez",
      date: "2 days ago",
    },
  ];

  const topEmployees = [
    {
      name: "Sarah Johnson",
      position: "Senior Developer",
      tasksCompleted: 38,
      photo: "/api/placeholder/40/40",
      rating: 4.9,
    },
    {
      name: "Michael Chen",
      position: "Backend Engineer",
      tasksCompleted: 32,
      photo: "/api/placeholder/40/40",
      rating: 4.7,
    },
    {
      name: "Emily Wilson",
      position: "UX Designer",
      tasksCompleted: 29,
      photo: "/api/placeholder/40/40",
      rating: 4.8,
    },
    {
      name: "David Smith",
      position: "Frontend Developer",
      tasksCompleted: 27,
      photo: "/api/placeholder/40/40",
      rating: 4.6,
    },
  ];

  const systemStatus = [
    {
      service: "API Services",
      status: "Operational",
      uptime: "99.99%",
      color: "text-green-600",
    },
    {
      service: "Database Cluster",
      status: "Operational",
      uptime: "99.95%",
      color: "text-green-600",
    },
    {
      service: "Authentication Service",
      status: "Operational",
      uptime: "99.98%",
      color: "text-green-600",
    },
    {
      service: "Storage Services",
      status: "Degraded",
      uptime: "98.75%",
      color: "text-amber-600",
    },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "LOW":
        return "bg-gray-100 text-gray-800";
      case "MEDIUM":
        return "bg-blue-100 text-blue-800";
      case "HIGH":
        return "bg-orange-100 text-orange-800";
      case "URGENT":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "bg-blue-100 text-blue-800";
      case "IN_PROGRESS":
        return "bg-yellow-100 text-yellow-800";
      case "PENDING":
        return "bg-purple-100 text-purple-800";
      case "RESOLVED":
        return "bg-green-100 text-green-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow p-6 flex items-center justify-between transition-all hover:shadow-md"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {stat.value}
                    </p>
                    <div
                      className={`flex items-center mt-1 ${
                        stat.isPositive ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {stat.isPositive ? (
                        <ArrowUp size={16} />
                      ) : (
                        <ArrowDown size={16} />
                      )}
                      <span className="text-xs font-medium ml-1">
                        {stat.change} from last month
                      </span>
                    </div>
                  </div>
                  <div className={`rounded-full p-3 ${stat.color}`}>
                    {stat.icon}
                  </div>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Pie Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Project Status Distribution
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={projectStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {projectStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value} Projects`, null]}
                      />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Bar Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Tickets by Department
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart
                      data={ticketsByDepartmentData}
                      barSize={20}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="open" name="Open" fill="#3b82f6" />
                      <Bar
                        dataKey="inProgress"
                        name="In Progress"
                        fill="#f59e0b"
                      />
                      <Bar dataKey="resolved" name="Resolved" fill="#10b981" />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Recent Tickets Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h2 className="font-semibold text-gray-800 flex items-center">
                  <AlertCircle size={18} className="mr-2 text-gray-600" />
                  Recent Tickets
                </h2>
                <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                  View All
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-3 text-left">ID</th>
                      <th className="px-6 py-3 text-left">Title</th>
                      <th className="px-6 py-3 text-center">Priority</th>
                      <th className="px-6 py-3 text-center">Status</th>
                      <th className="px-6 py-3 text-left">Assigned To</th>
                      <th className="px-6 py-3 text-right">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {recentTickets.map((ticket, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {ticket.id}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {ticket.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {ticket.client}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
                              ticket.priority
                            )}`}
                          >
                            {ticket.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              ticket.status
                            )}`}
                          >
                            {ticket.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {ticket.assignedTo}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-right">
                          {ticket.date}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case "employees":
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-800">
                Top Performing Employees
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {topEmployees.map((employee, index) => (
                  <div
                    key={index}
                    className="bg-white border rounded-lg p-4 flex flex-col items-center hover:shadow-md transition-all"
                  >
                    <img
                      src={employee.photo}
                      alt={employee.name}
                      className="w-16 h-16 rounded-full mb-2"
                    />
                    <h3 className="font-medium text-gray-900">
                      {employee.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      {employee.position}
                    </p>
                    <div className="flex items-center mb-1">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(employee.rating)
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-xs text-gray-500 ml-1">
                        {employee.rating}
                      </span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(employee.tasksCompleted / 40) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {employee.tasksCompleted} tasks completed
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case "system":
        return (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="font-semibold text-gray-800">System Status</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {systemStatus.map((service, index) => (
                    <div
                      key={index}
                      className="bg-white border rounded-lg p-4 flex items-center justify-between"
                    >
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {service.service}
                        </h3>
                        <p className={`text-sm ${service.color}`}>
                          {service.status}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-800">
                          {service.uptime}
                        </p>
                        <p className="text-xs text-gray-500">Uptime</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      {/* <div className="w-64 bg-white shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h1 className="text-xl font-bold text-gray-800">Admin Dashboard</h1>
        </div>
        <nav className="px-4 py-4">
          <ul className="space-y-1">
            <li>
              <button 
                onClick={() => setActiveTab('overview')}
                className={`w-full flex items-center px-4 py-2 text-sm rounded-md ${activeTab === 'overview' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <Layers size={18} className="mr-3" />
                Overview
              </button>
            </li>
            <li>
              <button 
                onClick={() => setActiveTab('employees')}
                className={`w-full flex items-center px-4 py-2 text-sm rounded-md ${activeTab === 'employees' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <Users size={18} className="mr-3" />
                Employees
              </button>
            </li>
            <li>
              <button 
                onClick={() => setActiveTab('system')}
                className={`w-full flex items-center px-4 py-2 text-sm rounded-md ${activeTab === 'system' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <Settings size={18} className="mr-3" />
                System Status
              </button>
            </li>
          </ul>
        </nav>
      </div> */}

      {/* Main Content */}
      <div className="flex-1">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className=" mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeTab === "overview" && "Dashboard Overview"}
                {activeTab === "employees" && "Employee Management"}
                {activeTab === "system" && "System Status"}
              </h2>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <button className="relative p-1 text-gray-400 hover:text-gray-500 focus:outline-none">
                    <Bell size={20} />
                    <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500"></span>
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <img
                    className="h-8 w-8 rounded-full"
                    src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT87nae4416RrOQAPy60JLL9bVfrTkAal_K7Q&s"
                    alt="User"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Intern Dashboard
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="mx-auto py-6">{renderActiveTabContent()}</main>
      </div>
    </div>
  );
};

export default InternDashboard;
